import { DndContext, type DragEndEvent, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToHorizontalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { BaseLineMinus } from '@/app/components/icons/BaseLineMinus';
import { BaseLinePlay } from '@/app/components/icons/BaseLinePlay';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { AUDIO_DURATION } from '@/app/constants';
import type { SegmentType } from '@/app/types';
import { cn, reportEvent } from '@/app/utils';
import { ROW_HEIGHT, SCALE_WIDTH, T2A_LIMIT, TIME_STEP } from '../../constant';
import { useDragMove } from '../../hooks/use-drag-move';
import { DraggableSegment } from './DraggableSegment';

interface TimelineEditorProps {
  /** 最大时长 */
  maxDuration?: number;
  /** 总时长 */
  duration?: number;
  /** 时间步长，默认为1（整数） */
  timeStep?: number;
  /** 时间片段数据源 */
  data?: SegmentType[];
  /** 刻度与刻度之间的间距，如果不传则自动计算使刻度等分容器宽度 */
  scaleWidth?: number;
  /** 每一个片段的高度 */
  rowHeight?: number;
  /** 数据变化时的回调函数 */
  onChange?: (segments: SegmentType[]) => void;
  /** 点击新建按钮时的回调函数 */
  onClickNewSegment?: (timeTag?: Partial<SegmentType>) => void;
  /** 音频时长变化时的回调函数 */
  onDurationChange?: (duration: number) => void;
}

export const TimelineEditor = ({
  maxDuration = AUDIO_DURATION,
  duration = AUDIO_DURATION,
  timeStep = TIME_STEP,
  data,
  scaleWidth,
  rowHeight = ROW_HEIGHT,
  onChange,
  onClickNewSegment,
  onDurationChange,
}: TimelineEditorProps) => {
  const t = useTranslations('text2audio');

  const containerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  const [segments, setSegments] = useState<SegmentType[]>(() => data || []);
  const [isResizing, setIsResizing] = useState<{ id: string; direction: 'left' | 'right' } | null>(null);
  const [currentDuration, setCurrentDuration] = useState<number>(() => duration);
  const [calculatedScaleWidth, setCalculatedScaleWidth] = useState<number>(SCALE_WIDTH);
  const [isDraggingDuration, setIsDraggingDuration] = useState(false);

  const actualScaleWidth = scaleWidth || calculatedScaleWidth;
  const totalWidth = maxDuration * actualScaleWidth;

  // 计算自适应的刻度宽度
  useEffect(() => {
    if (!scaleWidth && containerRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const containerWidth = entry.contentRect.width - 2;
          const newScaleWidth = containerWidth / maxDuration;
          setCalculatedScaleWidth(newScaleWidth);
        }
      });

      resizeObserver.observe(containerRef.current);
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [scaleWidth, maxDuration]);

  useEffect(() => {
    setSegments(data || []);
  }, [data]);

  useEffect(() => {
    setCurrentDuration(duration);
  }, [duration]);

  // 计算所有segments的最大结束时间
  const getMaxSegmentEndTime = () => {
    if (!segments.length) return 1;
    return Math.max(...segments.map((segment) => segment.endTime));
  };

  // 更新音频时长
  const updateDuration = (newDuration: number) => {
    setCurrentDuration(newDuration);
    onDurationChange?.(newDuration);
  };

  // 自动调整总时长以匹配最长的segment
  useEffect(() => {
    const maxEndTime = getMaxSegmentEndTime();
    // 如果有segment的结束时间超过当前总时长，自动延展总时长（不超过maxScale）
    if (maxEndTime > currentDuration) {
      const newDuration = Math.min(maxDuration, maxEndTime);
      updateDuration(newDuration);
    }
  }, [segments, currentDuration, maxDuration]);

  const updateSegments = (newSegments: SegmentType[]) => {
    setSegments(newSegments);
    onChange?.(newSegments);
  };

  // 处理增加音频时长
  const handleIncreaseDuration = () => {
    if (currentDuration < maxDuration) {
      const newDuration = Math.min(maxDuration, currentDuration + TIME_STEP);
      updateDuration(newDuration);
    }
  };

  // 处理减少音频时长
  const handleDecreaseDuration = () => {
    // 获取所有segment的最大结束时间
    const maxEndTime = getMaxSegmentEndTime();
    // 确保新时长不小于最大segment结束时间和1秒
    const minAllowedDuration = Math.max(1, maxEndTime);
    const newDuration = Math.max(minAllowedDuration, currentDuration - TIME_STEP);

    if (newDuration !== currentDuration) {
      updateDuration(newDuration);
    }
  };

  const snapToGrid = (value: number): number => {
    if (timeStep >= 1) {
      return Math.round(value);
    }

    if (timeStep > 0) {
      return Math.round(value / timeStep) * timeStep;
    }
    return value;
  };

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 5,
    },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 150, // 长按 150ms 后激活
      tolerance: 5, // 允许误差范围
    },
  });

  const sensors = useSensors(mouseSensor, touchSensor);

  // 渲染时间刻度
  const renderTimeScale = () => {
    const scales = [];
    for (let i = 0; i <= maxDuration; i++) {
      scales.push(
        <div
          key={`scale-${i}`}
          className="absolute select-none font-normal text-[8px] text-system-white64"
          style={{ left: `${i * actualScaleWidth - 2}px` }}
        >
          {i}
        </div>,
      );
    }
    return scales;
  };

  const gridLines = Array.from({ length: maxDuration + 1 }, (_, index) => ({
    id: `gridline-${index}`,
    position: index * actualScaleWidth,
  }));

  // 处理持续时间拖动
  const handleDurationDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    setIsDraggingDuration(true);
    reportEvent('drag_element_start', {
      element_name: 'duration_scale',
    });
  };

  const handleDurationDrag = (e: MouseEvent | TouchEvent) => {
    if (!isDraggingDuration || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const mouseX = clientX - containerRect.left;
    const timePosition = mouseX / actualScaleWidth;

    // 获取所有segment的最大结束时间
    const maxEndTime = getMaxSegmentEndTime();
    // 确保新时长不小于最大segment结束时间和1秒
    const minAllowedDuration = Math.max(1, maxEndTime);

    // 应用时间步长和约束
    const newDuration = snapToGrid(Math.min(maxDuration, Math.max(minAllowedDuration, timePosition)));

    if (newDuration !== currentDuration) {
      updateDuration(newDuration);
    }
  };

  const handleDurationDragEnd = () => {
    if (isDraggingDuration) {
      setIsDraggingDuration(false);
      onDurationChange?.(currentDuration);
      reportEvent('drag_element_end', {
        element_name: 'duration_scale',
        duration: currentDuration,
      });
    }
  };

  useDragMove({
    enabled: isDraggingDuration,
    onMove: handleDurationDrag,
    onEnd: handleDurationDragEnd,
  });

  // 渲染左右可选时间范围刻度
  const renderScale = (position: number, isEndPosition = false) => {
    return (
      <div
        className={cn(
          'absolute z-[1] h-full w-[2px] rounded-2 bg-[#6B7384]',
          isEndPosition && (isDraggingDuration ? 'cursor-grabbing' : 'cursor-grab'),
        )}
        style={{ left: `${position * actualScaleWidth}px` }}
        onMouseDown={isEndPosition ? handleDurationDragStart : undefined}
        onTouchStart={isEndPosition ? handleDurationDragStart : undefined}
      >
        <BaseLinePlay className="-top-2 -translate-x-1/2 absolute left-1/2 rotate-90 text-[#6B7384] text-xs" />
      </div>
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, delta } = event;
    const id = active.id as string;

    const deltaTimeUnits = delta.x / actualScaleWidth;

    const updatedSegments = segments.map((segment) => {
      if (segment.id === id) {
        const segmentWidth = (segment.endTime ?? 0) - (segment.startTime ?? 0);
        const newStartTime = snapToGrid(Math.max(0, (segment.startTime ?? 0) + deltaTimeUnits));
        const newEndTime = snapToGrid(Math.min(maxDuration, newStartTime + segmentWidth));

        if (newStartTime >= 0 && newEndTime <= maxDuration) {
          return {
            ...segment,
            startTime: newStartTime,
            endTime: newEndTime,
          };
        }
      }
      return segment;
    });

    updateSegments(updatedSegments);
  };

  const handleResizeStart = (id: string, direction: 'left' | 'right', e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    setIsResizing({ id, direction });
  };

  const handleResizeMove = (e: MouseEvent | TouchEvent) => {
    if (!isResizing || !containerRef.current) return;

    const { id, direction } = isResizing;
    const containerRect = containerRef.current.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const mouseX = clientX - containerRect.left;
    const timePosition = mouseX / actualScaleWidth;

    const updatedSegments = segments.map((segment) => {
      if (segment.id === id) {
        if (direction === 'left') {
          const snappedPosition = snapToGrid(timePosition);
          const newStartTime = Math.max(
            0,
            Math.min((segment.endTime ?? 0) - T2A_LIMIT.INTERVAL_LIMIT, snappedPosition),
          );
          return { ...segment, startTime: newStartTime };
        }

        const snappedPosition = snapToGrid(timePosition);
        const newEndTime = Math.min(
          maxDuration,
          Math.max((segment.startTime ?? 0) + T2A_LIMIT.INTERVAL_LIMIT, snappedPosition),
        );
        return { ...segment, endTime: newEndTime };
      }
      return segment;
    });

    updateSegments(updatedSegments);
  };

  const handleResizeEnd = () => {
    setIsResizing(null);
  };

  useDragMove({
    enabled: !!isResizing,
    onMove: handleResizeMove,
    onEnd: handleResizeEnd,
  });

  const handleDelete = (id: string) => {
    const updatedSegments = segments.filter((segment) => segment.id !== id);
    updateSegments(updatedSegments);
    reportEvent('delete_element_click', {
      element_name: 'button_time_stamp_track',
    });
  };

  return (
    <div className="flex min-h-[340px] w-full flex-1 flex-col space-y-4 rounded-12 bg-black/[24%] p-4">
      <div className="flex items-center justify-between">
        <div className="font-normal text-sm text-system-white leading-[1.4]">{t('timelineEditor.title')}</div>
        <div className="flex h-9 items-center gap-2">
          <div
            className={cn(
              'flex size-9 cursor-pointer items-center justify-center rounded-full bg-system-bg03 backdrop-blur-[30px] hover:bg-system-hover01',
              currentDuration <= 1 ? 'cursor-not-allowed text-white/[.12]' : 'text-white',
            )}
            onClick={handleDecreaseDuration}
          >
            <BaseLineMinus className="text-base" />
          </div>

          <div className="flex h-full w-20 select-none items-center justify-center gap-1 overflow-hidden rounded-[100px] bg-system-bg03 backdrop-blur-[30px]">
            <input
              type="number"
              value={currentDuration}
              className="h-full w-[35%] min-w-[1ch] flex-shrink-0 appearance-none border-none bg-transparent p-0 text-center font-normal text-sm text-white [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden"
              min={1}
              max={duration}
              step={timeStep}
              disabled
            />
            <span className="font-normal text-sm text-system-white48">s</span>
          </div>

          <div
            className={cn(
              'flex size-9 cursor-pointer items-center justify-center rounded-full bg-system-bg03 backdrop-blur-[30px] hover:bg-system-hover01',
              currentDuration >= maxDuration
                ? 'cursor-not-allowed text-white/[.12] hover:bg-system-bg03'
                : 'text-white',
            )}
            onClick={handleIncreaseDuration}
          >
            <BaseLinePlus className="text-base" />
          </div>
        </div>
      </div>

      <div ref={containerRef} className="mt-4 flex w-full flex-1 flex-col overflow-auto px-[6px]">
        <div className="relative flex h-3 items-center" style={{ width: `${totalWidth}px` }}>
          {/* 时间刻度 */}
          {renderTimeScale()}
        </div>

        <div className="relative mt-4 h-full flex-1" style={{ width: `${totalWidth}px` }} ref={timelineRef}>
          <div className="absolute inset-0">
            {/* 时间线和网格背景 */}
            {gridLines.map((line) => (
              <div
                key={line.id}
                className="absolute h-full w-px bg-white/[.12]"
                style={{ left: `${line.position}px` }}
              />
            ))}
            {/* 在0刻度位置添加区间起始图标 */}
            {renderScale(0)}
            {/* 在当前时长位置添加区间结束图标 */}
            {renderScale(currentDuration, true)}
          </div>

          <DndContext
            sensors={sensors}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToParentElement, restrictToHorizontalAxis]}
          >
            <div className="relative h-full w-full">
              {segments.map((segment, index) => (
                <DraggableSegment
                  key={segment.id}
                  segment={segment}
                  scaleWidth={actualScaleWidth}
                  onResizeStart={handleResizeStart}
                  rowHeight={rowHeight}
                  index={index}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </DndContext>

          {/* 新建按钮 */}
          <div
            className="absolute left-0 flex h-10 cursor-pointer items-center justify-between rounded-10 border-[0.5px] border-white/[.48] border-dashed bg-black/[.32] px-4 font-normal text-sm text-white/[.48] leading-[1.4] backdrop-blur-[2px]"
            style={{
              top: segments.length === 0 ? '0' : `${segments.length * (rowHeight + 4)}px`,
              width: `${currentDuration * actualScaleWidth}px`,
            }}
            onClick={() => {
              onClickNewSegment?.({ endTime: currentDuration });
              reportEvent('create_element_click', {
                element_name: 'button_time_stamp_track',
              });
            }}
          >
            <span className="select-none truncate">{t('timelineEditor.fillPrompt')}</span>
            <BaseLinePlus className="flex-shrink-0 text-base text-white" />
          </div>
        </div>
      </div>
    </div>
  );
};
